import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCurrency } from "../context/CurrencyContext";

import { Image_URL, API_BASE_URL } from "../config";

// Map country -> subdomain. Add more entries as new country sites go live.
const COUNTRY_DOMAIN_MAP = {
    NZ: "nz.westendjewellery.com",
    UK: "uk.westendjewellery.com",
    US: "uk.westendjewellery.com",
};
const DEFAULT_DOMAIN = "westendjewellery.com";

function getRawGroupName(search) {
    if (!search || !search.startsWith("?")) return null;
    const raw = search.slice(1); // drop leading "?"
    const groupMatch = raw.match(/(?:^|&)group=([^&]*)/);
    if (!groupMatch) return null;

    const groupStart = raw.indexOf("group=") + "group=".length;
    // Everything after "group=" up to the next KNOWN param key, or end of string
    const knownParams = ["categoryId", "subcategoryId", "collectionId", "shape", "stone", "metal", "style", "subcategory"];
    const knownParamPattern = new RegExp(`&(${knownParams.join("|")})=`);
    const afterGroup = raw.slice(groupStart);
    const cutIndex = afterGroup.search(knownParamPattern);
    const rawGroupValue = cutIndex === -1 ? afterGroup : afterGroup.slice(0, cutIndex);

    try {
        return decodeURIComponent(rawGroupValue);
    } catch {
        return rawGroupValue;
    }
}

const ProductGroup = ({ user }) => {
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const shapeFromURL = queryParams.get("shape");
    const { selectedCurrency } = useCurrency();
    const itemsPerPage = 12;
    const [, setShapedata] = useState([]);
    const [, setproductswise] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOption, setSortOption] = useState("default");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedSubCategory, setSelectedSubCategory] = useState("");
    const [selectedShape, setSelectedShape] = useState("");
    const [selectedProductShape, setSelectedProductShape] = useState({});
    const [selectedCarat, setSelectedCarat] = useState({}); // keyed by product.uniqueKey -> caratData entry's productCode
    const [hoveredShape, setHoveredShape] = useState({});
    const [expandedShapeList, setExpandedShapeList] = useState({});
    const [, setSelectedStyle] = useState(""); // eslint-disable-line no-unused-vars
    const [, setCategories] = useState([]); // eslint-disable-line no-unused-vars
    const [, setSubCategories] = useState([]); // eslint-disable-line no-unused-vars
    const [, setShapes] = useState([]); // eslint-disable-line no-unused-vars
    const [loadingImages, setLoadingImages] = useState(false);
    const [, setSelectedStone] = useState(""); // eslint-disable-line no-unused-vars
    const [, setSelectedMetal] = useState(""); // eslint-disable-line no-unused-vars
    const [wishlist, setWishlist] = useState([]);
    const [, setViewVersion] = useState(0);
    const groupName = getRawGroupName(location.search) || queryParams.get("group");

    const allowedMetals = useMemo(
        () => [
            "white gold",
            "yellow gold",
            "rose gold",
            "silver(925)",
            "silver",
            "platinum(950)",
            "platinum",
            "platinum 950",
        ],
        []
    );

    // QR-landing guard: whether the user is logged in at all is already
    // handled upstream by ProtectedRoute (via App.js's GetProfile session
    // check, which reads the shared auth_token cookie across every
    // subdomain) — this component only mounts once that's confirmed, so
    // `user` here is trustworthy. All this needs to do is make sure the
    // subdomain matches the logged-in user's country.
    useEffect(() => {
        if (!user) return; // ProtectedRoute guarantees this shouldn't happen, but stay safe

        const currentPath = `${location.pathname}${location.search}`;
        const currentHost = window.location.hostname;
        const country = user.country?.toUpperCase();
        const expectedDomain = COUNTRY_DOMAIN_MAP[country] || DEFAULT_DOMAIN;

        if (expectedDomain !== currentHost) {
            window.location.href = `https://${expectedDomain}${currentPath}`;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, location.pathname, location.search]);

    // Sync filters from the URL and fetch matching products
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const category = queryParams.get("category") || "";
        const shape = queryParams.get("shape") || "";
        const stone = queryParams.get("stone") || "";
        const metal = queryParams.get("metal") || "";
        const style = queryParams.get("style") || "";

        setSelectedCategory(category);
        setSelectedSubCategory(queryParams.get("subcategory") || "");
        setSelectedShape(shape);
        setSelectedStone(stone);
        setSelectedMetal(metal);
        setSelectedStyle(style);
        setCurrentPage(1);

        if (shape || stone || metal || category || style) {
            const normalizedMetal = metal && /(gold|platinum|silver)/i.test(metal)
                ? `9K ${metal}`
                : metal;
            handleShapeFilter(shape, stone, normalizedMetal, category, style);
        } else {
            fetchProductData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const applyMarkup = (base) => {
        const view = localStorage.getItem("activeView") || "wholesale";
        if (view === "wholesale") return base;

        const storedPayload = JSON.parse(localStorage.getItem("priceTable_seller") || "{}");
        const table = Array.isArray(storedPayload.data) ? storedPayload.data : [];

        if (!table.length) return base;

        const range = table.find((r) => {
            const from = parseFloat(r.from || 0);
            const to = r.to === null ? Infinity : parseFloat(r.to);
            return base >= from && base <= to;
        });

        if (!range) return base;

        // FIX: fall back to 1 (no-op multiplier), not 0, when markup is
        // missing/falsy — otherwise the price silently becomes 0.
        const markupPercent = parseFloat(range.markup || 1);
        return base * markupPercent;
    };

    const getBaseCaratValue = (product) => {
        const allCaratEntries = product.caratData || [];
        const baseCaratItem = allCaratEntries.find(
            (c) => c.productCode === product.productCode
        );
        return Number(baseCaratItem?.stoneCrt || 0);
    };

    const parseTrailingValue = (value) => {
        if (!value) return null;
        const parts = value.split(",");
        const num = parseFloat(parts[parts.length - 1]);
        return Number.isNaN(num) ? null : num;
    };

    // ✅ UPDATED: Product code specific image binding
    const getFilteredImages = (product, shapeOrStoneOverride, metalOverride, targetProductCode) => {
    if (!product.images || product.images.length === 0) return null;

    // 1. First priority: exact match on this variant's own product code
    if (targetProductCode) {
        const codeMatches = product.images.filter(
            (img) =>
                img.imgProdcutCode?.trim().toLowerCase() ===
                targetProductCode.trim().toLowerCase()
        );

        if (codeMatches.length > 0) {
            let metalMatches = codeMatches;

            if (metalOverride) {
                const normalizedMetal = metalOverride.trim().toLowerCase();
                const exactMetalMatches = codeMatches.filter((img) =>
                    img.value?.toLowerCase().includes(normalizedMetal)
                );
                if (exactMetalMatches.length > 0) metalMatches = exactMetalMatches;
            }

            if (metalMatches.length > 1) {
                metalMatches = [...metalMatches].sort((a, b) => {
                    const av = parseTrailingValue(a.value);
                    const bv = parseTrailingValue(b.value);
                    if (av == null) return 1;
                    if (bv == null) return -1;
                    return av - bv;
                });
            }

            return metalMatches[0];
        }
    }

    // 2. Fallback below — unchanged, only reached if this exact code has no images
    const defaultShape = product.shapeProductCodeData?.find(
        (s) => s.shapeName?.toLowerCase().trim() === "round"
    )?.shapeName || product.shapeProductCodeData?.[0]?.shapeName;

    const currentTarget = shapeOrStoneOverride || defaultShape;
    if (!currentTarget) return product.images[0];

    const normalizedTarget = currentTarget.trim().toLowerCase();

    const stoneSuffixMap = {
        "blue sapphire": "bs",
        "red ruby": "ru",
        "green emerald": "em"
    };

    let shapeMatches = [];

    if (stoneSuffixMap[normalizedTarget]) {
        const targetSuffix = stoneSuffixMap[normalizedTarget];
        shapeMatches = product.images.filter((img) => {
            const imgCode = img.imgProdcutCode?.trim().toLowerCase() || "";
            return imgCode.endsWith(targetSuffix);
        });
    }

    if (shapeMatches.length === 0) {
        shapeMatches = product.images.filter((img) => {
            const imgValue = img.value?.trim().toLowerCase() || "";
            const shapeName = product.selectedShape || defaultShape || "";
            return imgValue.includes(shapeName.trim().toLowerCase());
        });
    }

    if (shapeMatches.length === 0) return product.images[0];

    let metalMatches = shapeMatches;
    if (metalOverride) {
        const normalizedMetal = metalOverride.trim().toLowerCase();
        const exactMetalMatches = shapeMatches.filter((img) =>
            img.value?.toLowerCase().includes(normalizedMetal)
        );
        if (exactMetalMatches.length > 0) metalMatches = exactMetalMatches;
    }

    if (metalMatches.length > 1) {
        metalMatches = [...metalMatches].sort((a, b) => {
            const av = parseTrailingValue(a.value);
            const bv = parseTrailingValue(b.value);
            if (av == null) return 1;
            if (bv == null) return -1;
            return av - bv;
        });
    }

    return metalMatches[0];
};

    useEffect(() => {
        const handleStorageChange = () => setViewVersion((v) => v + 1);
        const handleViewChange = () => setViewVersion((v) => v + 1);

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener("viewChange", handleViewChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("viewChange", handleViewChange);
        };
    }, []);

    const fetchProductData = useCallback(async () => {
        try {
            setLoadingImages(true);
            const url = `${API_BASE_URL}/ProductDetails/GetProductGroupList?groupName=${encodeURIComponent(groupName)}`;

            const response = await fetch(url);
            const result = await response.json();

            if (result.success && result.data) {
                setproductswise(result.data);
                setProducts(result.data);

                const initialSelected = {};
                result.data.forEach((product) => {
                    const matchedYellowGold = product.productRelatedData?.find(
                        (item) => item.relatedItemKey.toLowerCase() === "yellow gold"
                    );

                    if (matchedYellowGold) {
                        initialSelected[product.id] = matchedYellowGold.relatedItemKey;
                    } else {
                        const fallback = product.productRelatedData?.find((item) =>
                            allowedMetals.includes(item.relatedItemKey.toLowerCase())
                        );
                        if (fallback) {
                            initialSelected[product.id] = fallback.relatedItemKey;
                        }
                    }
                });
                setSelectedMaterial(initialSelected);

                setCategories(
                    [...new Set(result.data.map((p) => p.categoryName))].map((name) => ({
                        id: name,
                        name,
                    }))
                );

                setSubCategories(
                    [...new Set(result.data.map((p) => p.subCategoryName))].map((name) => ({
                        id: name,
                        name,
                    }))
                );

                setShapes(
                    [...new Set(result.data.map((p) => p.shapeName))].map((name) => ({
                        id: name,
                        name,
                    }))
                );
            }
        } catch (error) {
            console.error("Error fetching product data:", error);
        } finally {
            setLoadingImages(false);
        }
    }, [allowedMetals, groupName]);

    const filteredProducts = useMemo(() => {
        return products.filter((p) => {
            const search = searchTerm.toLowerCase();
            const matchesName = p.productName?.toLowerCase().includes(search);
            const matchesMainCode = p.productCode?.toLowerCase().includes(search);
            const matchCodesArray = p.productCodesData?.some((item) =>
                item.productCode?.toLowerCase().includes(search)
            );

            const matchesSearch = matchesName || matchesMainCode || matchCodesArray;

            const matchesCategory = selectedCategory
                ? String(p.categoryName || "").trim().toLowerCase() === selectedCategory.trim().toLowerCase()
                : true;

            const matchesSubCategory = selectedSubCategory
                ? String(p.subCategoryName || "").trim().toLowerCase() === selectedSubCategory.trim().toLowerCase()
                : true;

            const matchesShape = selectedShape
                ? p.shapeName?.split(",").map((s) => s.trim().toLowerCase()).some((s) => s === selectedShape.trim().toLowerCase())
                : true;

            return matchesSearch && matchesCategory && matchesSubCategory && matchesShape;
        });
    }, [products, searchTerm, selectedCategory, selectedSubCategory, selectedShape]);

    const getEffectivePrice = useCallback((product) => {
        const selectedMetalKey = selectedMaterial[product.id];
        const metalObj = product.productRelatedData?.find(
            (m) => m.relatedItemKey === selectedMetalKey
        );

        const basePrice = metalObj?.price ?? product.productPrice;
        return applyMarkup(basePrice * selectedCurrency.price);
    }, [selectedMaterial, selectedCurrency]);

    const sortedProducts = useMemo(() => {
        return [...filteredProducts]
            .filter((p) => {
                if (sortOption === "wishlist") {
                    return wishlist.some((w) => w.ProductID === p.id);
                }
                return true;
            })
            .sort((a, b) => {
                if (sortOption === "latest") return new Date(b.createdOn) - new Date(a.createdOn);
                if (sortOption === "instock") return (b.inStock === "Yes") - (a.inStock === "Yes");
                if (sortOption === "BestSeller") {
                    const isBestSeller = (tag) => {
                        if (!tag) return 0;
                        return tag.toLowerCase().replace(/\s/g, "") === "bestseller" ? 1 : 0;
                    };
                    return isBestSeller(b.websiteTag) - isBestSeller(a.websiteTag);
                }
                if (sortOption === "lowToHigh") return getEffectivePrice(a) - getEffectivePrice(b);
                if (sortOption === "highToLow") return getEffectivePrice(b) - getEffectivePrice(a);
                return 0;
            });
    }, [filteredProducts, sortOption, wishlist, getEffectivePrice]);

    const expandedProducts = useMemo(() => {
        const expanded = [];
        sortedProducts.forEach((product) => {
            const shapes = product.shapeProductCodeData || [];
            if (shapes.length === 0) {
                expanded.push({ ...product, selectedShape: null, uniqueKey: `${product.id}-default` });
            } else {
                shapes.forEach((shapeObj) => {
                    expanded.push({
                        ...product,
                        selectedShape: shapeObj.shapeName,
                        productCode: shapeObj.productCode || shapeObj.ProductCode || product.productCode,
                        uniqueKey: `${product.id}-${shapeObj.shapeName}`
                    });
                });
            }
        });

        return expanded.sort((a, b) => getBaseCaratValue(a) - getBaseCaratValue(b));
    }, [sortedProducts]);

    const totalPages = Math.ceil(expandedProducts.length / itemsPerPage);
    const paginatedProducts = expandedProducts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber);
    };

    const handleShapeFilter = async (shape, stone, metal, category, style) => {
        try {
            setLoadingImages(true);
            const params = new URLSearchParams();
            if (shape?.trim()) params.append("shapeName", shape.trim());
            if (stone?.trim()) params.append("stoneName", stone.trim());
            if (metal?.trim()) params.append("metalName", metal.trim());
            if (category?.trim()) params.append("categoryName", category.trim());
            if (style?.trim()) params.append("styleName", style.trim());

            const response = await fetch(`${API_BASE_URL}/Product/GetProductListbyshape?${params.toString()}`);
            const result = await response.json();

            if (result.success && result.data) {
                setShapedata(result.data);
                setProducts(result.data);

                const initialSelected = {};
                result.data.forEach((product) => {
                    if (metal) {
                        const matchedMetal = product.productRelatedData?.find(
                            (item) => item.relatedItemKey.toLowerCase() === metal.toLowerCase()
                        );
                        if (matchedMetal) {
                            initialSelected[product.id] = matchedMetal.relatedItemKey;
                            return;
                        }
                    }

                    const matchedYellowGold = product.productRelatedData?.find(
                        (item) => item.relatedItemKey.toLowerCase().includes("yellow gold")
                    );
                    if (matchedYellowGold) {
                        initialSelected[product.id] = matchedYellowGold.relatedItemKey;
                        return;
                    }

                    const fallback = product.productRelatedData?.find((item) =>
                        allowedMetals.includes(item.relatedItemKey.toLowerCase())
                    );
                    if (fallback) {
                        initialSelected[product.id] = fallback.relatedItemKey;
                    }
                });

                setSelectedMaterial(initialSelected);
                setCategories([...new Set(result.data.map(p => p.categoryName))].map(name => ({ id: name, name })));
                setSubCategories([...new Set(result.data.map(p => p.subCategoryName))].map(name => ({ id: name, name })));
                setShapes([...new Set(result.data.map(p => p.shapeName))].map(name => ({ id: name, name })));
            }
        } catch (error) {
            console.error("Error fetching filtered product data:", error);
        } finally {
            setLoadingImages(false);
        }
    };

    useEffect(() => {
        const fetchWishlist = async () => {
            const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
            const userId = storedUser?.userId || 0;
            const companyId = storedUser?.companyId || 0;

            if (!userId) return;

            const res = await fetch(`${API_BASE_URL}/Order/GetWishlist?userId=${userId}&CompanyId=${companyId}`);
            const data = await res.json();

            const items = data.map((item) => ({
                WishlistID: item.wishlistID,
                ProductID: item.productID,
                ProductCode: item.productName,
                MetalKT: item.metalName,
                Shape: item.shape,
            }));

            setWishlist(items);
        };

        fetchWishlist();
    }, []);

    if (!selectedCurrency) return null;

    return (
        <div className="home">
            <div id="page" className="hfeed page-wrapper">
                <Header />
                <div id="site-main" className="site-main">
                    <div id="main-content" className="main-content">
                        <div id="primary" className="content-area">
                            <div id="content" className="site-content" role="main">
                                <div className="">
                                    <div className="section-container p-l-r">
                                        <div className="row">
                                            <div className="col-xl-12 col-lg-12 col-md-12 col-12">
                                                <div className="products-topbar clearfix flex justify-between items-center" style={{ marginBottom: '10px' }}>
                                                    <div className="products-topbar-left flex items-center gap-4" style={{ marginTop: '30px' }}>
                                                        <p style={{ textAlign: "center", fontSize: '18px' }}> {groupName} </p>
                                                        <div className="products-count container">
                                                            Showing {paginatedProducts.length} of {expandedProducts.length} results
                                                        </div>
                                                    </div>
                                                    <div style={{ display: "flex", whiteSpace: "nowrap", paddingBottom: "8px", marginTop: '30px' }}>
                                                        &nbsp;&nbsp;&nbsp;
                                                        <div className="products-topbar-right" style={{ marginLeft: 'auto', display: 'flex' }}>
                                                            <div style={{ position: 'relative' }}>
                                                                <i className="icon-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 2, fontSize: '16px' }}></i>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Search products..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => {
                                                                        setSearchTerm(e.target.value);
                                                                        setCurrentPage(1);
                                                                    }}
                                                                    className="border rounded-lg px-3 py-2 w-64 products-topbar-center flex"
                                                                    style={{ paddingLeft: '40px', lineHeight: '25px', borderRadius: '5px', border: 'none', boxShadow: '1px 1px 5px #ccc' }}
                                                                />
                                                            </div>
                                                            &nbsp; &nbsp;
                                                            <div className="products-sort dropdown">
                                                                <span className="sort-toggle dropdown-toggle" data-toggle="dropdown">
                                                                    {sortOption === "default" ? "Default sorting" : sortOption}
                                                                </span>
                                                                <ul className="sort-list dropdown-menu">
                                                                    <li className={sortOption === "default" ? "active" : ""}><a href="#0" onClick={() => setSortOption("default")}>Default sorting</a></li>
                                                                    <li className={sortOption === "latest" ? "active" : ""}><a href="#0" onClick={() => setSortOption("latest")}>Latest</a></li>
                                                                    <li className={sortOption === "lowToHigh" ? "active" : ""}><a href="#0" onClick={() => setSortOption("lowToHigh")}>Low to High</a></li>
                                                                    <li className={sortOption === "highToLow" ? "active" : ""}><a href="#0" onClick={() => setSortOption("highToLow")}>High to Low</a></li>
                                                                    <li className={sortOption === "instock" ? "active" : ""}><a href="#0" onClick={() => setSortOption("instock")}>In Stock</a></li>
                                                                    <li className={sortOption === "BestSeller" ? "active" : ""}><a href="#0" onClick={() => setSortOption("BestSeller")}>Best Seller</a></li>
                                                                    <li className={sortOption === "wishlist" ? "active" : ""}><a href="#0" onClick={() => setSortOption("wishlist")}>My Wishlist</a></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Product Grid */}
                                                <div className="tab-content">
                                                    <div className="tab-pane fade show active" id="layout-grid">
                                                        {loadingImages ? (
                                                            <div className="loading-spinner">
                                                                <div className="spinner"></div>
                                                            </div>
                                                        ) : (
                                                            <div className="products-list grid">
                                                                <div className="row">
                                                                    {paginatedProducts.map((product, productIndex) => {
                                                                        const selectedMetalKey = selectedMaterial[product.id];
                                                                        const metalOptions = product.productRelatedData?.filter((item) =>
                                                                            allowedMetals.includes(item.relatedItemKey?.toLowerCase())
                                                                        );
                                                                        const selectedMetal = metalOptions?.find(
                                                                            (item) => item.relatedItemKey === selectedMetalKey
                                                                        );
                                                                        const metalName = selectedMetal?.relatedItemKey || "";

                                                                        const activeShape = hoveredShape[product.uniqueKey] || selectedProductShape[product.uniqueKey] || product.selectedShape;

                                                                        const allCaratEntries = product.caratData || [];

                                                                        const baseCaratItem = allCaratEntries.find(
                                                                            c => c.productCode === product.productCode
                                                                        );

                                                                        const caratSiblingsRaw = baseCaratItem?.ShapeId != null
                                                                            ? allCaratEntries.filter(c => c.ShapeId === baseCaratItem.ShapeId)
                                                                            : (baseCaratItem ? [baseCaratItem] : []);

                                                                        const caratSiblings = [...caratSiblingsRaw].sort(
                                                                            (a, b) => Number(a.stoneCrt || 0) - Number(b.stoneCrt || 0)
                                                                        );

                                                                        const activeCaratCode = selectedCarat[product.uniqueKey];
                                                                        const selectedCaratItem =
                                                                            caratSiblings.find(c => c.productCode === activeCaratCode) || baseCaratItem;

                                                                        // ✅ Bind image explicitly to the selected carat product code
                                                                        const activeProductCode = selectedCaratItem?.productCode || product.productCode;
                                                                        const selectedImage = getFilteredImages(product, activeShape, selectedMetalKey, activeProductCode);

                                                                        const isAboveFold = productIndex < 4;

                                                                        return (
                                                                            <div className="col-xl-3 col-lg-4 col-md-3 col-sm-6" key={product.uniqueKey}>
                                                                                <div className="products-entry clearfix product-wapper">
                                                                                    <div className="products-thumb" style={{ height: '100%' }}>
                                                                                        {product.inStock === "Yes" && (
                                                                                            <div className="product-lable" id="in-stock">
                                                                                                <div className="hot" style={{ backgroundColor: "#1b8f1d" }}>In-stock</div>
                                                                                            </div>
                                                                                        )}
                                                                                        {product.websiteTag === "BestSeller" && (
                                                                                            <div className="product-lable">
                                                                                                <div className="onsale">Best Seller</div>
                                                                                            </div>
                                                                                        )}

                                                                                        <div className="product-thumb-hover">
                                                                                            <Link
                                                                                                to={`/productdetails/${product.id}?metal=${encodeURIComponent(`9K ${metalName}`)}${product.selectedShape
                                                                                                    ? `&productCode=${encodeURIComponent(selectedCaratItem?.productCode || "")}&shape=${encodeURIComponent(product.selectedShape)}&carat=${encodeURIComponent(Number(selectedCaratItem?.stoneCrt || 0).toFixed(2))}`
                                                                                                    : shapeFromURL ? `&shape=${encodeURIComponent(shapeFromURL)}` : ""
                                                                                                    }`}
                                                                                            >
                                                                                                <img
                                                                                                    src={`${Image_URL}UatProduct/${selectedImage?.imageNames?.[0] || "default.png"}`}
                                                                                                    className="post-image"
                                                                                                    alt={product.productName}
                                                                                                    loading={isAboveFold ? "eager" : "lazy"}
                                                                                                    decoding="async"
                                                                                                    fetchpriority={isAboveFold ? "high" : "auto"}
                                                                                                />
                                                                                                <img
                                                                                                    src={`${Image_URL}UatProduct/${selectedImage?.imageNames?.[1] || selectedImage?.imageNames?.[0] || "default.png"}`}
                                                                                                    className="hover-image back"
                                                                                                    alt={product.productName}
                                                                                                    loading="lazy"
                                                                                                    decoding="async"
                                                                                                />
                                                                                            </Link>
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="product-button" id="gold-div">
                                                                                        <div className="stone-option-container">
<div className="stone-options">
    {(() => {
        // Extract unique shapes from shapeProductCodeData (falling back to shapeName split by comma)
        const shapesList = product.shapeProductCodeData?.length > 0
            ? [...new Set(product.shapeProductCodeData.map(s => s.shapeName?.trim()).filter(Boolean))]
            : (product.shapeName ? product.shapeName.split(",").map(s => s.trim()).filter(Boolean) : []);

        const isExpanded = expandedShapeList[product.id];
        const visibleShapes = isExpanded ? shapesList : shapesList.slice(0, 3);

        return (
            <>
                {visibleShapes.map((shape) => (
                    <button
                        key={`${product.uniqueKey}-${shape}`}
                        className={`stone-option-btn ${(selectedProductShape?.[product.uniqueKey] || product.selectedShape) === shape ? "active" : ""}`}
                        onClick={() => {
                            setSelectedProductShape((prev) => ({
                                ...prev,
                                [product.uniqueKey]: shape,
                            }));
                        }}
                        onMouseEnter={() =>
                            setHoveredShape((prev) => ({ ...prev, [product.uniqueKey]: shape }))
                        }
                        onMouseLeave={() =>
                            setHoveredShape((prev) => {
                                const copy = { ...prev };
                                delete copy[product.uniqueKey];
                                return copy;
                            })
                        }
                    >
                        <img
                            src={`https://westendjewellery.com/assets/img/${shape.toLowerCase().replace(/\s+/g, "")}.png`}
                            width="20"
                            height="20"
                            style={{ objectFit: "contain", display: "block" }}
                            alt={shape}
                            title={shape}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${Image_URL}UatProduct/default.png`;
                            }}
                        />
                    </button>
                ))}

                {!isExpanded && shapesList.length > 3 && (
                    <button
                        type="button"
                        className="stone-option-btn more-shapes-btn"
                        onClick={() =>
                            setExpandedShapeList((prev) => ({
                                ...prev,
                                [product.id]: true,
                            }))
                        }
                    >
                        +{shapesList.length - 3}
                    </button>
                )}
            </>
        );
    })()}
</div>
                                                                                            <p className="selected-stone-text">
                                                                                                <span id="selected-stone">
                                                                                                    {activeProductCode}
                                                                                                </span>

                                                                                                <br />

                                                                                                <span id="selected-stone">
                                                                                                    {caratSiblings.length > 1 ? (
                                                                                                        <select
                                                                                                            value={selectedCarat[product.uniqueKey] || baseCaratItem?.productCode || ""}
                                                                                                            onChange={(e) => {
                                                                                                                const newCode = e.target.value;
                                                                                                                setSelectedCarat((prev) => ({
                                                                                                                    ...prev,
                                                                                                                    [product.uniqueKey]: newCode,
                                                                                                                }));
                                                                                                            }}
                                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                                            style={{ fontSize: 13, padding: "2px 4px" }}
                                                                                                        >
                                                                                                            {caratSiblings.map((caratItem) => (
                                                                                                                <option key={caratItem.productCode} value={caratItem.productCode}>
                                                                                                                    {Number(caratItem.stoneCrt || 0).toFixed(2)}
                                                                                                                </option>
                                                                                                            ))}
                                                                                                        </select>
                                                                                                    ) : (
                                                                                                        Number(selectedCaratItem?.stoneCrt || 0).toFixed(2)
                                                                                                    )}
                                                                                                </span>

                                                                                                <br />

                                                                                                <span style={{ fontSize: 13 }} id="selected-stone">
                                                                                                    {selectedCurrency?.name === "GBP"
                                                                                                        ? selectedCurrency?.name
                                                                                                        : `${selectedCurrency?.name} $`}{" "}
                                                                                                    {/* FIX: multiply by the selected currency rate BEFORE the
                                                                                                        markup, same as Product.js — the carat price used to
                                                                                                        skip the currency conversion entirely. */}
                                                                                                    {applyMarkup(
                                                                                                        Number(selectedCaratItem?.productPrice || 0) * (selectedCurrency?.price || 1)
                                                                                                    ).toFixed(0)}
                                                                                                </span>
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Pagination */}
                                                <div className="pagination flex justify-center mt-10 mb-10">
                                                    <ul className="flex flex-wrap items-center gap-3">
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className={`pageBtns ${currentPage === 1 ? "disabled" : ""}`}
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                            >
                                                                Prev
                                                            </button>
                                                        </li>
                                                        {[...Array(totalPages)].map((_, i) => (
                                                            <li key={i}>
                                                                <button
                                                                    type="button"
                                                                    className={`pageNum ${currentPage === i + 1 ? "active" : ""}`}
                                                                    onClick={() => handlePageChange(i + 1)}
                                                                >
                                                                    {i + 1}
                                                                </button>
                                                            </li>
                                                        ))}
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className={`pageBtns ${currentPage === totalPages ? "disabled" : ""}`}
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                            >
                                                                Next
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default ProductGroup;